import "./PatientFiltersComponent.scss";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import CommonService from "../../../shared/services/common.service";
import {Field, FieldProps, Form, Formik, FormikProps, FormikValues} from "formik";
import FormikAutoCompleteDropdown
    from "../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import {APIConfig} from "../../../constants";
import FormikDatePickerComponent from "../../../shared/components/core/formik-date-picker/FormikDatePickerComponent";
import ButtonComponent from "../../../shared/components/core/button/ButtonComponent";
import HeadingComponent from "../../../shared/components/heading/headingComponent";
import formValidationRules from "../../../shared/services/validation.service";
import * as Yup from "yup";
import useFetch from "../../../shared/hooks/useFetch";

interface PatientFiltersComponentProps {
    filtersData: any;
    onFiltersApply: (filterData: any) => void;
    clearAllFilters: () => void;
}

const validationSchema = Yup.object().shape({
    startDate: Yup.mixed().nullable().when("endDate", {
        is: (endDate: Date) => {
            return Boolean(endDate)
        },
        then: Yup.mixed().nullable().required("Start date is required"),
        otherwise: Yup.mixed().nullable().notRequired()
    }),
    endDate: Yup.mixed().nullable().when("startDate", {
        is: (startDate: Date) => {
            return Boolean(startDate)
        },
        then: Yup.mixed().nullable().required("End date is required"),
        otherwise: Yup.mixed().nullable().notRequired()
    }),
    facilityIds: Yup.array().nullable().notRequired(),
    locationIds: Yup.object().nullable().notRequired(),
}, [['startDate', 'endDate']]);

const PatientFiltersComponent = (props: PatientFiltersComponentProps) => {
    const {filtersData, onFiltersApply, clearAllFilters} = props;
    const [dataFiltersInitialState, setDataFiltersInitialState] = useState(filtersData);
    const {
        userList,
        facilityListLite
    } = useSelector((state: IRootReducerState) => state.staticData);
    const formikRef = useRef<FormikProps<FormikValues>>(null);
    const {
        response: locationList = [],
        loading: isLocationListLoading,
        loadingFailed: isLocationListLoadingFailed,
        loaded: isLocationListLoaded,
        fetchData: fetchLocationList
    } = useFetch<any>();

    const getLocationList = useCallback((selectedFacilities: any[]) => {
        const facilityIds = selectedFacilities.map((facility: any) => facility?.id);
        const payload = {facilityIds: facilityIds, isActive: true};

        formikRef?.current?.setFieldValue('locationObjs', []);

        fetchLocationList({
            url: APIConfig.FACILITY_LOCATION.URL,
            method: APIConfig.FACILITY_LOCATION.METHOD,
            payload
        })
    }, [fetchLocationList]);

    const onFiltersApplyHandler = useCallback((values: any) => {
        const payload = {
            ...values
        }
        payload.genders = payload.gendersObjs.map((item: any) => item.code);
        payload.cities = payload.citiesObjs.map((item: any) => item.city);
        payload.createdByIds = payload.createdByObjs.map((item: any) => item.id);
        payload.startDate = payload.startDate ? CommonService.convertDateFormat(values['startDate'], 'YYYY-MM-DD') : "";
        payload.endDate = payload.endDate ? CommonService.convertDateFormat(values['endDate'], 'YYYY-MM-DD') : "";
        payload.facilityIds = payload.facilityObjs.map((item: any) => item.id);
        payload.locationIds = payload.locationObjs.map((item: any) => item.id);
        onFiltersApply(payload);
    }, [onFiltersApply]);

    useEffect(() => {
        setDataFiltersInitialState(filtersData);
    }, [filtersData]);

    return (
        <div className={'patient-filters-component screen t-form-screen'}>
            <>
                <div className="drawer-sub-header horizontal-vertical-center-align">
                    <HeadingComponent title={"Filters"}/>
                    <div className={'filters-clear-all-button'}
                         onClick={() => {
                             formikRef.current && formikRef.current.resetForm();
                             clearAllFilters();
                         }}
                    >
                        Clear all
                    </div>
                </div>
                <div className="t-form t-form-drawer">
                    <Formik
                        initialValues={dataFiltersInitialState}
                        enableReinitialize={true}
                        onSubmit={onFiltersApplyHandler}
                        innerRef={formikRef}
                        validationSchema={validationSchema}
                    >
                        {({isSubmitting, values, isValid, setFieldValue, validateForm, errors, dirty}) => {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            useEffect(() => {
                                validateForm();
                            }, [values, validateForm]);
                            return (
                                <Form noValidate={true} className={"patient-filters-holder"}>
                                    <div className="t-form-controls-wrapper">
                                        <Field name={'gendersObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        label={'Gender'}
                                                        placeholder={'Select gender'}
                                                        valueKey={'code'}
                                                        multiple={true}
                                                        data={CommonService._staticData.genderOptions}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option.title) || '')}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'citiesObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        label={'City'}
                                                        placeholder={"start type for city"}
                                                        formikField={field}
                                                        url={APIConfig.CITY_LIST.URL}
                                                        method={APIConfig.CITY_LIST.METHOD}
                                                        searchMode={'serverSide'}
                                                        openOnFocus={true}
                                                        dataListKey={"data.docs"}
                                                        data={[]}
                                                        multiple={true}
                                                        valueKey={'city'}
                                                        getOptionLabel={(option => (option.city) || '')}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'createdByObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Added by'}
                                                        multiple={true}
                                                        data={userList}
                                                        valueKey={"id"}
                                                        placeholder={'Select Added by'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option.firstName + ' ' + option.lastName) || '')}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'startDate'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        label={'Start Date'}
                                                        formikField={field}
                                                        placeholder={'Select End Date'}
                                                        maxDate={formValidationRules.today}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'endDate'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        label={'End Date'}
                                                        formikField={field}
                                                        placeholder={'Select End Date'}
                                                        maxDate={formValidationRules.today}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'facilityObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Facility Name'}
                                                        multiple={true}
                                                        data={facilityListLite}
                                                        valueKey={"id"}
                                                        placeholder={'Select Facility Name'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option.name) || '')}
                                                        onUpdate={(value) => {
                                                            getLocationList(value);
                                                        }}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'locationObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Location Name'}
                                                        multiple={true}
                                                        data={locationList?.length > 0 ? locationList : []}
                                                        isDataLoading={isLocationListLoading}
                                                        isDataLoaded={isLocationListLoaded}
                                                        disabled={values?.facilityObjs?.length < 1}
                                                        isDataLoadingFailed={isLocationListLoadingFailed}
                                                        valueKey={"id"}
                                                        noDataMessage={'No Location found for facility'}
                                                        placeholder={'Select Location Name'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option.name) || '')}
                                                    />
                                                )
                                            }
                                        </Field>

                                    </div>
                                    <div className="drawer-actions form-action-section">
                                        <ButtonComponent variant={"contained"}
                                                         fullWidth={true}
                                                         type={"submit"}
                                                         disabled={!isValid || !dirty}
                                        >
                                            Apply
                                        </ButtonComponent>
                                    </div>
                                </Form>
                            )
                        }
                        }
                    </Formik>
                </div>
            </>

        </div>
    );

};

export default PatientFiltersComponent;
